import { type JSX, useEffect, useMemo, useRef, useState } from 'react';
import QRCode from 'qrcode';

import { MobiLauncherConfig } from '@schema/index.js';

import {
  deleteDevice,
  fetchConfig,
  listDevices,
  listTelemetryEvents,
  type PortalDevice,
  updateDevice,
} from '../api.js';
import {
  buildDeploymentGuide,
  buildDeploymentPack,
} from './deployment-pack.js';
import { confirmDialog } from './dialogs.js';

export interface DevicesPanelProps {
  /** Launcher URL the QR encodes — e.g. `https://app.mobilauncher.com/t/acme`. */
  launcherUrl: string;
  /** Tenant ID for the PNG filename + print-sheet header. */
  tenantId: string;
  /**
   * Edit token for the registered-devices listing. `undefined` = no auth
   * context (standalone Builder preview → "sign in" message); `null` = a
   * non-owner admin (list loads via the session cookie); a string = the owner
   * bearer.
   */
  editToken?: string | null;
  /** Viewer mode — the device list renders read-only (no rename/tag/remove). */
  readOnly?: boolean;
}

/**
 * QR-code provisioning helper for fleet rollouts. The Android sideload
 * APK has a "Scan QR code" button on first launch; that flow accepts
 * any URL we encode here, so an admin can print a sticker per device
 * and tap-scan instead of typing the URL on every install.
 *
 * Everything renders client-side — no server endpoint, no Stripe-style
 * webhook plumbing. The `qrcode` library is ~10 KB minified and
 * generates a Canvas (for PNG export) and SVG (for in-page rendering)
 * from the same source string.
 *
 * Three actions exposed:
 *   - Show QR for the launcher URL (preview)
 *   - Download as PNG (single sticker)
 *   - Open a print-friendly sheet (multiple identical stickers per
 *     page; browser's native print dialog handles the rest)
 */
export function DevicesPanel({
  launcherUrl,
  tenantId,
  editToken,
  readOnly = false,
}: DevicesPanelProps): JSX.Element {
  const [svgMarkup, setSvgMarkup] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);

  useEffect(() => {
    let cancelled = false;
    setError(null);
    // High error-correction (`H`) lets the QR survive a centered logo
    // overlay or partial sticker damage. 4× margin is the spec minimum
    // for reliable scanning; smaller margins make rugged-device cameras
    // hunt for the quiet zone.
    QRCode.toString(launcherUrl, {
      type: 'svg',
      errorCorrectionLevel: 'H',
      margin: 4,
      color: { dark: '#0b0c0f', light: '#ffffff' },
    })
      .then((markup) => {
        if (cancelled) return;
        setSvgMarkup(markup);
      })
      .catch((e: unknown) => {
        if (cancelled) return;
        setError(e instanceof Error ? e.message : 'QR generation failed');
      });
    return () => {
      cancelled = true;
    };
  }, [launcherUrl]);

  // Hidden canvas used for the PNG export — kept off-screen so it
  // doesn't compete with the SVG preview for layout space.
  useEffect(() => {
    if (!canvasRef.current) return;
    QRCode.toCanvas(canvasRef.current, launcherUrl, {
      errorCorrectionLevel: 'H',
      margin: 4,
      // 1024px is sticker-sheet-ready — sharp at 4" prints on most
      // home/office laser printers.
      width: 1024,
      color: { dark: '#0b0c0f', light: '#ffffff' },
    }).catch(() => {
      /* error already surfaced via the SVG branch */
    });
  }, [launcherUrl]);

  function downloadPng(): void {
    const canvas = canvasRef.current;
    if (!canvas) return;
    canvas.toBlob((blob) => {
      if (!blob) return;
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `mobilauncher-${tenantId}-qr.png`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    }, 'image/png');
  }

  function openPrintSheet(): void {
    if (svgMarkup === null) return;
    // 12-up print sheet on a US Letter page (4 × 3 grid, ~2.5" each).
    // The popup uses window.print() so the customer's browser handles
    // the printer-driver dialog — no PDF generation, no extra deps.
    const win = window.open('', '_blank', 'width=900,height=1100');
    if (!win) {
      setError('Pop-up blocked. Allow pop-ups for this site to use the print sheet.');
      return;
    }
    const escapedTenant = tenantId.replace(/[&<>"']/g, (c) => {
      const map: Record<string, string> = {
        '&': '&amp;',
        '<': '&lt;',
        '>': '&gt;',
        '"': '&quot;',
        "'": '&#39;',
      };
      return map[c] ?? c;
    });
    win.document.write(`<!doctype html>
<html>
<head>
  <meta charset="utf-8">
  <title>MobiLauncher QR stickers — ${escapedTenant}</title>
  <style>
    @page { size: letter; margin: 0.5in; }
    * { box-sizing: border-box; }
    html, body { margin: 0; padding: 0; font-family: system-ui, sans-serif; }
    .header { padding: 0 0 0.4in; text-align: center; color: #4a5568; font-size: 11pt; }
    .header strong { color: #1a2940; }
    .grid {
      display: grid;
      grid-template-columns: repeat(3, 1fr);
      gap: 0.2in;
      page-break-inside: avoid;
    }
    .sticker {
      border: 1px dashed #cbd5e0;
      padding: 0.12in;
      text-align: center;
    }
    .sticker svg { width: 100%; height: auto; display: block; }
    .sticker__caption {
      margin-top: 0.08in;
      font-size: 8pt;
      color: #4a5568;
      word-break: break-all;
    }
    @media print {
      .header { color: #4a5568; }
      .sticker { border-color: #cbd5e0; }
      button { display: none; }
    }
    .toolbar {
      position: sticky;
      top: 0;
      background: #fff;
      padding: 0.4in 0 0.2in;
      text-align: center;
    }
    .toolbar button {
      font: inherit;
      padding: 0.4rem 1rem;
      background: #1976d2;
      color: white;
      border: none;
      border-radius: 0.25rem;
      cursor: pointer;
    }
    .toolbar button:hover { background: #1565c0; }
  </style>
</head>
<body>
  <div class="toolbar"><button onclick="window.print()">Print this sheet</button></div>
  <div class="header">
    <strong>MobiLauncher</strong> — tenant <strong>${escapedTenant}</strong> — 12 stickers per page
  </div>
  <div class="grid">
    ${Array.from({ length: 12 })
      .map(
        () => `<div class="sticker">${svgMarkup}<div class="sticker__caption">${escapedTenant}</div></div>`,
      )
      .join('')}
  </div>
</body>
</html>`);
    win.document.close();
  }

  return (
    <section className="devices-panel" data-testid="devices-panel">
      <div className="devices-panel__columns">
        <section className="devices-panel__col devices-panel__col--add">
          <header className="details__head devices-panel__col-head">
            <h3 className="details__title devices-panel__col-title">Add new devices</h3>
          </header>

          <div className="devices-qr">
            <div className="devices-qr__preview" aria-label="Launcher URL QR code">
              {error !== null && (
                <p className="details__bundle-err" role="alert">
                  {error}
                </p>
              )}
              {svgMarkup !== null && (
                // dangerouslySetInnerHTML is fine here — the SVG comes from
                // the qrcode library, not user input.
                <div
                  className="devices-qr__svg"
                  dangerouslySetInnerHTML={{ __html: svgMarkup }}
                />
              )}
              <p className="devices-qr__url-caption">
                Encodes:&nbsp;
                <code className="devices-qr__url-code">{launcherUrl}</code>
              </p>
            </div>

            <div className="devices-qr__actions">
              <button
                type="button"
                className="details__bundle"
                onClick={downloadPng}
                disabled={svgMarkup === null}
                data-testid="devices-qr-download"
              >
                Download PNG
              </button>
              <button
                type="button"
                className="details__bundle"
                onClick={openPrintSheet}
                disabled={svgMarkup === null}
                data-testid="devices-qr-print"
              >
                Print sheet (12-up)
              </button>
            </div>
            <p className="devices-qr__exports-hint">
              PNG is 1024 × 1024 (sharp at 4″ prints). The sheet prints
              12 stickers per page via your browser&apos;s print dialog.
            </p>

            <DeploymentPackButton tenantId={tenantId} launcherUrl={launcherUrl} />
          </div>

          <InstallGuide launcherUrl={launcherUrl} />
        </section>

        <section className="devices-panel__col devices-panel__col--list">
          {editToken !== undefined ? (
            <RegisteredDevices tenantId={tenantId} editToken={editToken} readOnly={readOnly} />
          ) : (
            <header className="details__head devices-panel__col-head">
              <h3 className="details__title devices-panel__col-title">Registered devices</h3>
              <p className="details__sub">
                Sign in to your tenant to see the live device list.
              </p>
            </header>
          )}
        </section>
      </div>

      {/* Hidden canvas for the PNG export. The SVG above is what users see. */}
      <canvas ref={canvasRef} style={{ display: 'none' }} />
    </section>
  );
}

interface RegisteredDevicesProps {
  tenantId: string;
  /** Owner bearer, or null for a non-owner admin (cookie auth). */
  editToken: string | null;
  /** Viewer mode — render the list without rename/tag/remove controls. */
  readOnly?: boolean;
}

/**
 * Live list of devices that have self-registered for this tenant. Every
 * boot of the launcher (APK or browser) POSTs to /devices/register; the
 * server also bumps `last_seen_at` on every config poll, so this list
 * doubles as a "is each kiosk actually online?" report.
 *
 * Refresh button instead of auto-polling: the customer is usually here
 * to investigate one device, not to watch the fleet in real time. A
 * background poll on the editor page would cost us request budget for
 * little value.
 */
function RegisteredDevices({ tenantId, editToken, readOnly = false }: RegisteredDevicesProps): JSX.Element {
  const [devices, setDevices] = useState<PortalDevice[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [query, setQuery] = useState('');
  // Track which row is in nickname-edit mode + the draft value.
  const [editing, setEditing] = useState<{ id: string; draft: string } | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  /**
   * Map of deviceId → ms epoch of that device's most recent telemetry
   * event. Populated by the same recent-events endpoint the Telemetry
   * tab uses (no extra server work). Refreshes alongside the device
   * list. Empty Map means "no telemetry data yet" — could mean the
   * tenant is below Enterprise OR devices haven't flushed yet.
   */
  const [telemetryByDevice, setTelemetryByDevice] = useState<Map<string, number>>(
    () => new Map(),
  );

  async function load(): Promise<void> {
    setLoading(true);
    setError(null);
    const result = await listDevices({ tenantId, editToken });
    setLoading(false);
    if (result.kind === 'ok') {
      setDevices(result.devices);
    } else {
      setDevices([]);
      if (result.kind === 'unauthorized') {
        setError('Edit token rejected — sign back in to reload.');
      } else if (result.kind === 'network') {
        setError(`Network: ${result.message}`);
      } else {
        setError(`Server returned ${String(result.status)}.`);
      }
      return;
    }
    // Fan out to telemetry: fetch the most recent 500 events, group by
    // attrs.deviceId, keep the max receivedAt per device. Tolerates an
    // empty / unauthorized response (Free / Pro tenants who don't
    // flush telemetry get an empty Map → row badges show "—").
    const tel = await listTelemetryEvents({ tenantId, editToken, limit: 500 });
    if (tel.kind === 'ok') {
      const m = new Map<string, number>();
      for (const e of tel.events) {
        if (e.attrs !== undefined && typeof (e.attrs as Record<string, unknown>)['deviceId'] === 'string') {
          const id = (e.attrs as Record<string, unknown>)['deviceId'] as string;
          const existing = m.get(id);
          if (existing === undefined || existing < e.receivedAt) m.set(id, e.receivedAt);
        }
      }
      setTelemetryByDevice(m);
    } else {
      setTelemetryByDevice(new Map());
    }
  }

  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenantId, editToken]);

  // Filter client-side. Sub-200-device fleets fit in memory comfortably;
  // server-side search is an exercise for when someone actually ships
  // 1000+ kiosks per tenant.
  const filtered = useMemo(() => {
    if (devices === null) return null;
    const q = query.trim().toLowerCase();
    if (q.length === 0) return devices;
    return devices.filter((d) => {
      const nickname = typeof d.metadata?.['nickname'] === 'string'
        ? (d.metadata['nickname'] as string)
        : '';
      return (
        d.id.toLowerCase().includes(q) ||
        nickname.toLowerCase().includes(q) ||
        (d.deviceName ?? '').toLowerCase().includes(q) ||
        (d.androidId ?? '').toLowerCase().includes(q) ||
        (d.city ?? '').toLowerCase().includes(q) ||
        (d.country ?? '').toLowerCase().includes(q)
      );
    });
  }, [devices, query]);

  async function saveNickname(deviceId: string, nickname: string): Promise<void> {
    setBusyId(deviceId);
    const trimmed = nickname.trim();
    const res = await updateDevice({
      tenantId,
      editToken,
      deviceId,
      nickname: trimmed.length === 0 ? null : trimmed,
    });
    setBusyId(null);
    setEditing(null);
    if (res.kind === 'ok') {
      // Optimistic-ish: re-list to pick up the new metadata.
      await load();
    } else {
      setError(
        res.kind === 'unauthorized'
          ? 'Edit token rejected.'
          : res.kind === 'network'
            ? `Network: ${res.message}`
            : res.kind === 'invalid'
              ? `Invalid: ${res.message}`
              : `Server returned ${String('status' in res ? res.status : '?')}.`,
      );
    }
  }

  async function saveTags(deviceId: string, tags: string[]): Promise<void> {
    setBusyId(deviceId);
    const res = await updateDevice({ tenantId, editToken, deviceId, tags });
    setBusyId(null);
    if (res.kind === 'ok') {
      await load();
    } else {
      setError(
        res.kind === 'unauthorized'
          ? 'Edit token rejected.'
          : res.kind === 'network'
            ? `Network: ${res.message}`
            : res.kind === 'invalid'
              ? `Invalid: ${res.message}`
              : `Server returned ${String('status' in res ? res.status : '?')}.`,
      );
    }
  }

  async function removeDevice(deviceId: string, label: string): Promise<void> {
    const ok = await confirmDialog({
      title: 'Remove this device?',
      message: `Remove "${label}" from the fleet? It will re-register on its next boot, but its historical data (first seen, boot count) will be lost.`,
      confirmLabel: 'Remove device',
      destructive: true,
      requireText: 'delete',
    });
    if (!ok) return;
    setBusyId(deviceId);
    const res = await deleteDevice({ tenantId, editToken, deviceId });
    setBusyId(null);
    if (res.kind === 'ok') {
      await load();
    } else {
      setError(
        res.kind === 'unauthorized'
          ? 'Edit token rejected.'
          : res.kind === 'network'
            ? `Network: ${res.message}`
            : `Server returned ${String('status' in res ? res.status : '?')}.`,
      );
    }
  }

  function exportCsv(): void {
    if (filtered === null || filtered.length === 0) return;
    const header = [
      'nickname',
      'device_name',
      'device_id',
      'android_id',
      'os_version',
      'security_patch',
      'app_version',
      'connection',
      'boot_count',
      'ip_address',
      'local_ipv4',
      'city',
      'country',
      'tags',
      'first_seen_iso',
      'last_seen_iso',
    ];
    const rows = filtered.map((d) => [
      nicknameOf(d),
      d.deviceName ?? '',
      d.id,
      d.androidId ?? '',
      d.osVersion ?? '',
      d.securityPatch ?? '',
      d.appVersion ?? '',
      d.androidId !== null ? 'APK' : 'Browser',
      String(d.bootCount),
      d.ipAddress ?? '',
      d.localIpv4 ?? '',
      d.city ?? '',
      d.country ?? '',
      tagsOf(d).join('; '),
      new Date(d.firstSeenAt * 1000).toISOString(),
      new Date(d.lastSeenAt * 1000).toISOString(),
    ]);
    const csv = [header, ...rows].map((r) => r.map(csvEscape).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `mobilauncher-${tenantId}-devices.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }

  return (
    <section className="devices-registered" data-testid="devices-registered">
      <header className="details__head devices-panel__col-head">
        <h3 className="details__title devices-panel__col-title">Registered devices</h3>
        <p className="details__sub">
          Every launcher boot self-registers here. <strong>Last seen</strong> bumps on
          every config poll, so it doubles as an &ldquo;is this kiosk online?&rdquo; check.
        </p>
      </header>

      <div className="devices-registered__toolbar">
        <input
          type="search"
          className="devices-registered__search"
          placeholder="Search nickname, name, ID, location…"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          data-testid="devices-registered-search"
        />
        <div className="devices-registered__toolbar-actions">
          <button
            type="button"
            className="details__bundle"
            onClick={exportCsv}
            disabled={filtered === null || filtered.length === 0}
            data-testid="devices-registered-csv"
          >
            Export CSV
          </button>
          <button
            type="button"
            className="details__bundle"
            onClick={() => void load()}
            disabled={loading}
            data-testid="devices-registered-refresh"
          >
            {loading ? 'Refreshing…' : 'Refresh'}
          </button>
        </div>
      </div>

      {error !== null && (
        <p className="details__bundle-err" role="alert">
          {error}
        </p>
      )}

      {devices !== null && devices.length === 0 && error === null && (
        <p className="details__hint">
          No devices have registered yet. Once a kiosk boots into this tenant —
          either the sideloaded APK or any browser at the launcher URL — it will
          show up here.
        </p>
      )}

      {filtered !== null && filtered.length === 0 && query.length > 0 && (
        <p className="details__hint">No devices match "{query}".</p>
      )}

      {filtered !== null && filtered.length > 0 && (
        <table className="devices-registered__table" data-testid="devices-registered-table">
          <thead>
            <tr>
              <th>Status</th>
              <th>Device</th>
              <th>OS / App</th>
              <th>Location</th>
              <th>IP address</th>
              <th>Boots</th>
              <th>Last seen</th>
              <th title="Most recent telemetry event from this device">Telemetry</th>
              <th aria-label="Actions" />
            </tr>
          </thead>
          <tbody>
            {filtered.map((d) => {
              const status = statusFor(d.lastSeenAt);
              const conn: 'APK' | 'Browser' = d.androidId !== null ? 'APK' : 'Browser';
              const nickname = nicknameOf(d);
              const displayName = nickname.length > 0 ? nickname : d.deviceName ?? '(unnamed)';
              const isEditing = editing?.id === d.id;
              const isBusy = busyId === d.id;
              return (
                <tr key={d.id} className={isBusy ? 'devices-registered__row--busy' : undefined}>
                  <td>
                    <span className={`devices-registered__pill devices-registered__pill--${status}`}>
                      {status}
                    </span>
                    <span className={`devices-registered__pill devices-registered__pill--${conn === 'APK' ? 'apk' : 'browser'}`}>
                      {conn}
                    </span>
                  </td>
                  <td>
                    {readOnly ? (
                      <span className="devices-registered__name">{displayName}</span>
                    ) : isEditing ? (
                      <input
                        autoFocus
                        type="text"
                        className="devices-registered__name-edit"
                        value={editing.draft}
                        onChange={(e) =>
                          setEditing((prev) => (prev ? { ...prev, draft: e.target.value } : null))
                        }
                        onBlur={() => void saveNickname(d.id, editing.draft)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') void saveNickname(d.id, editing.draft);
                          if (e.key === 'Escape') setEditing(null);
                        }}
                        placeholder="Nickname (e.g. Lobby kiosk)"
                        maxLength={80}
                      />
                    ) : (
                      <button
                        type="button"
                        className="devices-registered__name-btn"
                        onClick={() => setEditing({ id: d.id, draft: nickname })}
                        title="Click to rename"
                      >
                        <span className="devices-registered__name">{displayName}</span>
                        <span className="devices-registered__name-pencil" aria-hidden>✎</span>
                      </button>
                    )}
                    {nickname.length > 0 && d.deviceName !== null && (
                      <div className="devices-registered__meta">{d.deviceName}</div>
                    )}
                    <code className="devices-registered__id">{d.id}</code>
                    {d.androidId !== null && (
                      <div className="devices-registered__meta">
                        Android ID: <code>{d.androidId}</code>
                      </div>
                    )}
                    <DeviceTags
                      tags={tagsOf(d)}
                      busy={isBusy}
                      {...(readOnly
                        ? { readOnly: true }
                        : { onChange: (next: string[]) => void saveTags(d.id, next) })}
                    />
                  </td>
                  <td>
                    <div>{formatOs(d)}</div>
                    {d.securityPatch !== null && (
                      <div className="devices-registered__meta">Security patch {d.securityPatch}</div>
                    )}
                    <div className="devices-registered__meta">{d.appVersion ?? '—'}</div>
                  </td>
                  <td>{formatLocation(d) ?? '—'}</td>
                  <td>
                    {d.ipAddress !== null ? (
                      <code className="devices-registered__ip">{d.ipAddress}</code>
                    ) : (
                      '—'
                    )}
                    {d.localIpv4 !== null && (
                      <div className="devices-registered__meta">
                        LAN <code>{d.localIpv4}</code>
                      </div>
                    )}
                  </td>
                  <td title={`First seen ${formatRelative(d.firstSeenAt)}`}>
                    {d.bootCount}
                  </td>
                  <td>{formatRelative(d.lastSeenAt)}</td>
                  <td><TelemetryBadge lastMsEpoch={telemetryByDevice.get(d.id)} /></td>
                  <td>
                    {!readOnly && (
                      <button
                        type="button"
                        className="devices-registered__delete"
                        onClick={() => void removeDevice(d.id, displayName)}
                        disabled={isBusy}
                        aria-label={`Remove ${displayName}`}
                        title="Remove device"
                      >
                        ×
                      </button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
    </section>
  );
}

function nicknameOf(d: PortalDevice): string {
  const nick = d.metadata?.['nickname'];
  return typeof nick === 'string' ? nick : '';
}

function tagsOf(d: PortalDevice): string[] {
  const raw = d.metadata?.['tags'];
  return Array.isArray(raw) ? raw.filter((t): t is string => typeof t === 'string') : [];
}

/**
 * Inline tag chips + an add field for one device. Tags group devices for
 * pilot targeting; editing one PUTs the full replacement list.
 */
function DeviceTags({
  tags,
  busy,
  onChange,
  readOnly = false,
}: {
  tags: string[];
  busy: boolean;
  onChange?: (next: string[]) => void;
  /** Viewer mode — render the tag chips without add/remove controls. */
  readOnly?: boolean;
}): JSX.Element {
  const [draft, setDraft] = useState('');
  const add = (): void => {
    const t = draft.trim();
    setDraft('');
    if (t.length === 0 || tags.includes(t)) return;
    onChange?.([...tags, t]);
  };
  return (
    <div className="device-tags" data-testid="device-tags">
      {tags.map((t) => (
        <span key={t} className="device-tag">
          {t}
          {!readOnly && (
            <button
              type="button"
              className="device-tag__remove"
              onClick={() => onChange?.(tags.filter((x) => x !== t))}
              aria-label={`Remove tag ${t}`}
              disabled={busy}
            >
              ×
            </button>
          )}
        </span>
      ))}
      {readOnly && tags.length === 0 && (
        <span className="devices-registered__meta">No tags</span>
      )}
      {!readOnly && (
      <input
        className="device-tag__input"
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault();
            add();
          }
        }}
        onBlur={add}
        placeholder="+ tag"
        maxLength={60}
        disabled={busy}
        aria-label="Add tag"
      />
      )}
    </div>
  );
}

/**
 * Normalize the OS label. APK kiosks report a bare Android release ("16")
 * from Build.VERSION.RELEASE, while browser kiosks report a UA-derived label
 * ("Android 16", "macOS 10.15.7"). An APK device is always Android, so prefix
 * a bare value to match — without double-prefixing one that already says so.
 */
export function formatOs(d: PortalDevice): string {
  const os = d.osVersion;
  if (os === null || os.length === 0) return '—';
  const isAndroidApk = d.androidId !== null;
  if (isAndroidApk && !/^android/i.test(os)) return `Android ${os}`;
  return os;
}

type Status = 'online' | 'idle' | 'offline';

function statusFor(lastSeenAt: number): Status {
  const diff = Math.floor(Date.now() / 1000) - lastSeenAt;
  if (diff < 120) return 'online';
  if (diff < 900) return 'idle';
  return 'offline';
}

function formatLocation(d: PortalDevice): string | null {
  if (d.city !== null && d.country !== null) return `${d.city}, ${d.country}`;
  if (d.city !== null) return d.city;
  if (d.country !== null) return d.country;
  return null;
}

function formatRelative(unixSeconds: number): string {
  const diff = Math.floor(Date.now() / 1000) - unixSeconds;
  if (diff < 60) return 'just now';
  if (diff < 3600) return `${String(Math.floor(diff / 60))}m ago`;
  if (diff < 86400) return `${String(Math.floor(diff / 3600))}h ago`;
  if (diff < 2592000) return `${String(Math.floor(diff / 86400))}d ago`;
  return new Date(unixSeconds * 1000).toLocaleDateString();
}

function csvEscape(value: string): string {
  if (value.includes(',') || value.includes('"') || value.includes('\n')) {
    return `"${value.replace(/"/g, '""')}"`;
  }
  return value;
}

/**
 * Three install paths a customer can take. We surface them collapsed by
 * default — most repeat visitors already onboarded their devices and
 * don't need the wall of instructions every time.
 *
 * The first two paths (browser kiosk, MDM-managed) work today with no
 * extra dependencies. The sideload APK path requires the customer to
 * request the build by email — we don't have a public APK download yet
 * (deliberately, while the beta is hand-held).
 */
function InstallGuide({ launcherUrl }: { launcherUrl: string }): JSX.Element {
  return (
    <details className="devices-install">
      <summary className="devices-install__summary">
        How do I get this on a device?
      </summary>
      <ol className="devices-install__list">
        <li>
          <strong>Any Android browser (no install).</strong> Open the
          launcher URL above in Chrome / Firefox / Samsung Internet on the
          device. Add to the home screen for a one-tap launcher. This is
          the path most managed-browser MDM modes use — SOTI, Intune,
          Workspace ONE, Scalefusion, Hexnode all support &ldquo;lock to a
          URL&rdquo; out of the box.
        </li>
        <li>
          <strong>MDM-pushed managed browser.</strong> In your MDM&apos;s
          Lockdown / Kiosk policy, paste the launcher URL as the locked
          target. Devices pull the policy on next check-in and boot
          straight into the launcher.
        </li>
        <li>
          <strong>Sideload APK (full-screen, no browser chrome).</strong>{' '}
          Email{' '}
          <a href="mailto:mobilauncher@oakandashtech.com?subject=APK%20request">
            mobilauncher@oakandashtech.com
          </a>{' '}
          to request the current beta APK. After install:
          <ol>
            <li>
              Open MobiLauncher on the device. The first screen has a{' '}
              <strong>Scan QR code</strong> button.
            </li>
            <li>
              Scan the sticker from this page (or the print sheet). The
              app parses the tenant out of the URL and boots into your
              launcher.
            </li>
            <li>
              <em>Optional:</em> set as the device&apos;s default
              launcher (Settings → Apps → Default apps → Home app) so it
              survives reboots.
            </li>
          </ol>
        </li>
      </ol>
      <p className="details__hint devices-install__hint">
        Not sure which to pick? The browser path takes 30 seconds and
        works on any Android device made in the last decade. The APK
        is worth the extra step on shared / public-facing kiosks where
        you don&apos;t want users seeing browser UI.
      </p>
      <p className="details__hint devices-install__hint">
        URL for any MDM kiosk policy:{' '}
        <code className="devices-qr__url-code">{launcherUrl}</code>
      </p>
    </details>
  );
}

/**
 * "Download deployment pack" — fetches the tenant's current config,
 * builds a JSON file (apps, allowed packages, launcher URL) plus a
 * Markdown setup guide tailored to common MDMs (SOTI MobiControl,
 * Intune, Scalefusion, Hexnode, etc.), and pushes both as downloads.
 *
 * The JSON is MDM-agnostic — the same file drives setup in every
 * kiosk-capable MDM we've seen because they all use the same
 * primitives (kiosk URL, allowed-apps list, status-bar toggles). A
 * SOTI-specific XML exporter is a follow-up once we have MobiControl
 * access to validate against.
 */
function DeploymentPackButton({
  tenantId,
  launcherUrl,
}: {
  tenantId: string;
  launcherUrl: string;
}): JSX.Element {
  const [status, setStatus] = useState<'idle' | 'fetching' | 'error'>('idle');
  const [error, setError] = useState<string | null>(null);

  async function download(): Promise<void> {
    setStatus('fetching');
    setError(null);
    const res = await fetchConfig(tenantId);
    if (res.kind !== 'ok') {
      setStatus('error');
      setError(
        res.kind === 'not-found'
          ? `Config not found for tenant ${tenantId}.`
          : res.kind === 'network'
            ? `Network error: ${res.message}`
            : `Unexpected ${String(res.status)} response.`,
      );
      return;
    }
    // Validate so the pack only ever embeds a well-formed config — a
    // half-broken config would generate a half-broken pack.
    const parsed = MobiLauncherConfig.safeParse(res.config);
    if (!parsed.success) {
      setStatus('error');
      setError('Config did not parse — re-save in the Launcher tab and try again.');
      return;
    }
    const pack = buildDeploymentPack({ config: parsed.data, launcherUrl });
    const guide = buildDeploymentGuide(pack);

    // Two files: machine-readable JSON + human-readable Markdown.
    // Saving both means the customer hands either to their MDM admin
    // depending on whether they want to script the import or read it.
    downloadBlob(
      new Blob([JSON.stringify(pack, null, 2)], { type: 'application/json' }),
      `mobilauncher-deployment-${pack.tenantId}.json`,
    );
    downloadBlob(
      new Blob([guide], { type: 'text/markdown' }),
      `mobilauncher-deployment-${pack.tenantId}.md`,
    );
    setStatus('idle');
  }

  return (
    <div className="deployment-pack">
      <h4 className="deployment-pack__heading">For your MDM console</h4>
      <button
        type="button"
        className="details__bundle details__bundle--locked deployment-pack__btn"
        onClick={() => {
          void download();
        }}
        disabled={status === 'fetching'}
        data-testid="devices-deployment-pack"
      >
        {status === 'fetching' ? 'Preparing…' : '⤓ Download deployment pack'}
      </button>
      <p className="deployment-pack__hint">
        JSON (apps, allow-list, launcher URL) + Markdown setup guide
        covering SOTI MobiControl, Intune, Scalefusion, Hexnode, Esper.
      </p>
      {error !== null && (
        <p className="details__bundle-err" role="alert">
          {error}
        </p>
      )}
    </div>
  );
}

function downloadBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

/**
 * Tiny "Last telemetry: X ago" pip rendered in the Devices table.
 *   - never reported → muted "—"
 *   - reported >24h ago → amber pip
 *   - reported within 24h → green pip
 * Click target / hover title gives the exact ms epoch for diagnostics.
 */
function TelemetryBadge({ lastMsEpoch }: { lastMsEpoch: number | undefined }): JSX.Element {
  if (lastMsEpoch === undefined) {
    return <span className="devices-registered__telemetry devices-registered__telemetry--none">—</span>;
  }
  const diffMs = Date.now() - lastMsEpoch;
  const stale = diffMs > 24 * 60 * 60 * 1000;
  return (
    <span
      className={`devices-registered__telemetry devices-registered__telemetry--${stale ? 'stale' : 'fresh'}`}
      title={new Date(lastMsEpoch).toISOString()}
    >
      <span className="devices-registered__telemetry-pip" aria-hidden />
      {formatRelativeMs(diffMs)}
    </span>
  );
}

function formatRelativeMs(diffMs: number): string {
  const diffSec = Math.max(0, Math.round(diffMs / 1000));
  if (diffSec < 60) return `${String(diffSec)}s ago`;
  const diffMin = Math.round(diffSec / 60);
  if (diffMin < 60) return `${String(diffMin)}m ago`;
  const diffHr = Math.round(diffMin / 60);
  if (diffHr < 24) return `${String(diffHr)}h ago`;
  const diffDay = Math.round(diffHr / 24);
  return `${String(diffDay)}d ago`;
}
